import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Drill } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { PageHeader, LoadingState } from '@/components/common';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import { stagger, fadeUp } from '@/lib/animations';
import { wellsApi, type WellDetail as WellDetailData } from '@/api/services/wells.api';
import { getErrorMessage } from '@/api/client';
import { formatCurrency, formatDate, formatNumber } from '@/lib/utils';

/**
 * One well, and every job performed on it.
 *
 * This is the view the well exists for. Drilling, completion and workover are
 * separate contracts, often years apart, and "what has this well cost us" is a
 * question about all of them at once — one that could not be asked while the
 * well was five text fields repeated on each job.
 *
 * The cost shown is our consumption at our carrying value. It is deliberately
 * not presented as revenue or margin: billing is settled in the operator's ERP,
 * and a figure here that disagreed with theirs would be acted on by whoever saw
 * it first.
 */
export default function WellDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [well, setWell] = useState<WellDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    wellsApi
      .getById(id)
      .then((data) => !cancelled && setWell(data))
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        navigate('/wells');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) return <LoadingState variant="detail" />;
  if (!well) return null;

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Wells', href: '/wells' },
            { label: well.name },
          ]}
          title={well.name}
          description={`API ${well.apiNumber}${well.operatorName ? ` · ${well.operatorName}` : ''}`}
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <RailCard title="Well" icon={<Drill className="h-4 w-4 text-muted-foreground" />}>
              <DetailList
                items={[
                  { label: 'API', value: well.apiNumber },
                  { label: 'Well number', value: well.wellNumber, when: !!well.wellNumber },
                  { label: 'Operator', value: well.operatorName, when: !!well.operatorName },
                  { label: 'Lease', value: well.leaseName, when: !!well.leaseName },
                  { label: 'Field', value: well.fieldName, when: !!well.fieldName },
                  { label: 'Basin', value: well.basin, when: !!well.basin },
                  { label: 'Status', value: well.status.toLowerCase() },
                  { label: 'Yard', value: well.location?.name, when: !!well.location },
                ]}
              />
            </RailCard>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <h3 className="text-base font-semibold text-foreground">Across every job</h3>
                  <CardDescription>{well.lifetime.basis}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <Figure label="Jobs" value={formatNumber(well.lifetime.jobCount)} />
                    <Figure label="Charges" value={formatNumber(well.lifetime.chargeCount)} />
                    <Figure
                      label="Material consumed"
                      value={formatCurrency(well.lifetime.consumedCost)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <h3 className="text-base font-semibold text-foreground">Jobs on this well</h3>
                  <CardDescription>
                    Most recent first. Each is a separate piece of work with its own AFE.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2.5">
                  {well.jobs.length === 0 ? (
                    <p className="py-2 text-sm text-muted-foreground">
                      No jobs recorded against this well yet.
                    </p>
                  ) : (
                    well.jobs.map((job) => (
                      <div
                        key={job.id}
                        className="flex flex-wrap items-start justify-between gap-3 rounded-lg border p-3"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/jobs/${job.id}`}
                              className="font-medium text-foreground underline-offset-4 hover:underline"
                            >
                              {job.jobNumber}
                            </Link>
                            <Badge variant="outline">{job.status.toLowerCase()}</Badge>
                          </div>
                          <p className="mt-0.5 text-sm text-muted-foreground">{job.name}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {[
                              job.afeNumber && `AFE ${job.afeNumber}`,
                              job.plannedBaseMetric &&
                                `${formatNumber(job.plannedBaseMetric)} ${job.baseMetricUnit ?? ''}`.trim(),
                              job.startDate && formatDate(job.startDate),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium tabular-nums">
                            {formatCurrency(job.consumedCost)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {job.chargeCount} charge{job.chargeCount === 1 ? '' : 's'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
